import { type ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  // 'full' = 90% des Viewports (Breite + Hoehe) — fuer Chat-/Editor-Modals
  // die viel Platz brauchen. DocumentDetailModal ueberschreibt die Breite via
  // className mit !important fuer Vollbreite-Variante.
  full: 'w-[90vw] h-[90vh] max-w-none',
}

export interface ModalProps {
  open?: boolean
  /** Alias for open */
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  /** Alias for onOpenChange – accepts () => void for simple close handlers */
  onClose?: () => void
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: ModalSize
  /** Blendet den Close-Button (X oben rechts) aus. */
  hideCloseButton?: boolean
  className?: string
  /** Ueberschreibt die Klassen des inneren Content-Wrappers (default: p-5 overflow-y-auto).
   *  Fuer full-height Inhalte wie eingebettete Chat-Seiten: "p-0 overflow-hidden flex flex-col min-h-0" */
  contentClassName?: string
}

/**
 * Standard-Modal basierend auf Radix Dialog.
 * Ersetzt alle Portal/Overlay-Eigenbauten. Fuer Bestaetigungs-Dialoge bitte
 * `ConfirmDialog` weiterverwenden – der baut intern auf diesem Modal auf.
 */
export function Modal({
  open,
  isOpen,
  onOpenChange,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideCloseButton,
  className,
  contentClassName,
}: ModalProps) {
  const resolvedOpen = open ?? isOpen ?? false
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      onClose?.()
      onOpenChange?.(false)
    } else {
      onOpenChange?.(true)
    }
  }
  return (
    <Dialog.Root open={resolvedOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-overlay animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)]',
            'bg-white rounded-lg shadow-modal z-modal flex flex-col max-h-[90vh]',
            'animate-zoom-in-95 focus:outline-none',
            sizeClass[size],
            className,
          )}
        >
          {(title || !hideCloseButton) && (
            <div className="px-5 py-3 border-b border-gray-200 flex items-start justify-between gap-3 flex-shrink-0">
              <div className="flex flex-col gap-0.5">
                {title ? (
                  <Dialog.Title className="text-h4 text-gray-900">{title}</Dialog.Title>
                ) : null}
                {description ? (
                  <Dialog.Description className="text-small text-gray-500">
                    {description}
                  </Dialog.Description>
                ) : null}
              </div>
              {!hideCloseButton && (
                <Dialog.Close
                  className="p-1 text-gray-400 hover:text-gray-600 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                  aria-label="Schließen"
                >
                  <X className="h-4 w-4" />
                </Dialog.Close>
              )}
            </div>
          )}
          <div className={cn('p-5 overflow-y-auto flex-1 min-h-0 text-body text-gray-700', contentClassName)}>{children}</div>
          {footer ? (
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-2">
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
